// src/pages/CreditCustomersPage.jsx
import React, { useState, useEffect } from 'react';
import { Users, Plus, Search, AlertCircle, CheckSquare, Square } from 'lucide-react';
import { creditCustomersApi } from '../api';

const CreditCustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  // 新增/编辑客户弹窗
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // 结清弹窗
  const [settleCustomer, setSettleCustomer] = useState(null);
  const [unsettledSales, setUnsettledSales] = useState([]);
  const [selectedSaleIds, setSelectedSaleIds] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [settling, setSettling] = useState(false);

  useEffect(() => { loadCustomers(); }, []);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const res = await creditCustomersApi.getList();
      if (res.success) setCustomers(res.data || []);
    } catch (err) {
      console.error('加载记账客户失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c => {
    if (!searchKeyword) return true;
    const kw = searchKeyword.toLowerCase();
    return c.name?.toLowerCase().includes(kw) || c.phone?.includes(kw);
  });

  const totalUnsettled = customers.reduce((sum, c) => sum + parseFloat(c.unsettled_total || 0), 0);

  // ==================== 新增/编辑 ====================
  const openCreate = () => {
    setEditingCustomer(null);
    setForm({ name: '', phone: '', notes: '' });
    setFormError('');
    setShowFormDialog(true);
  };

  const openEdit = (customer) => {
    setEditingCustomer(customer);
    setForm({ name: customer.name, phone: customer.phone || '', notes: customer.notes || '' });
    setFormError('');
    setShowFormDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError('客户名称不能为空'); return; }
    setSaving(true);
    setFormError('');
    try {
      let res;
      if (editingCustomer) {
        res = await creditCustomersApi.update(editingCustomer.id, form);
      } else {
        res = await creditCustomersApi.create(form);
      }
      if (res.success) {
        setShowFormDialog(false);
        loadCustomers();
      } else {
        setFormError(res.message || '保存失败');
      }
    } catch (err) {
      setFormError(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  // ==================== 结清 ====================
  const openSettle = async (customer) => {
    setSettleCustomer(customer);
    setSelectedSaleIds([]);
    setLoadingSales(true);
    try {
      const res = await creditCustomersApi.getUnsettledSales(customer.id);
      if (res.success) setUnsettledSales(res.data || []);
    } catch (err) {
      console.error('加载未结清订单失败:', err);
    } finally {
      setLoadingSales(false);
    }
  };

  const toggleSelectSale = (id) => {
    setSelectedSaleIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedSaleIds(
      selectedSaleIds.length === unsettledSales.length ? [] : unsettledSales.map(s => s.id)
    );
  };

  const selectedTotal = unsettledSales
    .filter(s => selectedSaleIds.includes(s.id))
    .reduce((sum, s) => sum + parseFloat(s.total_amount || 0), 0);

  const handleSettle = async () => {
    if (selectedSaleIds.length === 0) return;
    setSettling(true);
    try {
      const res = await creditCustomersApi.settleSales(settleCustomer.id, selectedSaleIds);
      if (res.success) {
        setSettleCustomer(null);
        loadCustomers();
      } else {
        alert(res.message || '结清失败');
      }
    } catch (err) {
      alert(err.message || '结清失败');
    } finally {
      setSettling(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold">记账客户管理</h2>
          <p className="text-xs text-gray-600 mt-1">维护可以被赊账的客户名单，查看欠款、批量结清</p>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700">
          <Plus className="w-4 h-4" />新增记账客户
        </button>
      </div>

      {/* 汇总卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-xs text-gray-500">记账客户数</p>
          <p className="text-2xl font-bold mt-1">{customers.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-xs text-gray-500">有未结清欠款的客户</p>
          <p className="text-2xl font-bold mt-1">{customers.filter(c => parseFloat(c.unsettled_total || 0) > 0).length}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-xs text-gray-500">未结清总金额</p>
          <p className="text-2xl font-bold mt-1 text-red-600">${totalUnsettled.toFixed(2)}</p>
        </div>
      </div>

      {/* 搜索 */}
      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input type="text" placeholder="搜索客户名称/电话"
          value={searchKeyword} onChange={e => setSearchKeyword(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
      </div>

      {/* 客户列表 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">还没有记账客户</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-gray-500 text-left">
                <th className="px-4 py-3">客户</th>
                <th className="px-4 py-3">电话</th>
                <th className="px-4 py-3 text-right">未结清笔数</th>
                <th className="px-4 py-3 text-right">未结清金额</th>
                <th className="px-4 py-3">状态</th>
                <th className="px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map(c => {
                const unsettled = parseFloat(c.unsettled_total || 0);
                return (
                  <tr key={c.id} className={`border-t border-gray-100 ${unsettled > 0 ? 'bg-red-50/30' : ''}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{c.name}</p>
                      {c.notes && <p className="text-xs text-gray-400">{c.notes}</p>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{c.phone || '—'}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{c.unsettled_count || 0}</td>
                    <td className={`px-4 py-3 text-right font-medium ${unsettled > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      ${unsettled.toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                        {c.status === 'active' ? '启用中' : '已停用'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button onClick={() => openEdit(c)} className="text-blue-600 hover:underline text-xs">编辑</button>
                        {unsettled > 0 && (
                          <button onClick={() => openSettle(c)} className="text-green-600 hover:underline text-xs font-medium">
                            结清
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 新增/编辑客户弹窗 */}
      {showFormDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h3 className="font-bold text-lg">{editingCustomer ? '编辑记账客户' : '新增记账客户'}</h3>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">客户名称 <span className="text-red-500">*</span></label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">电话</label>
                <input type="text" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">备注</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" rows={2} />
              </div>
              {editingCustomer && (
                <div>
                  <label className="block text-sm font-medium mb-1">状态</label>
                  <select value={form.status || editingCustomer.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                    <option value="active">启用中</option>
                    <option value="inactive">停用</option>
                  </select>
                </div>
              )}
              {formError && <p className="text-xs text-red-500">{formError}</p>}
            </div>
            <div className="flex justify-end gap-3 p-6 border-t">
              <button onClick={() => setShowFormDialog(false)} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">取消</button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 结清弹窗 */}
      {settleCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="p-6 border-b flex-shrink-0">
              <h3 className="font-bold text-lg">结清 —— {settleCustomer.name}</h3>
              <p className="text-xs text-gray-500 mt-1">勾选要结清的订单，可以一次选中好几笔一起标记</p>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {loadingSales ? (
                <p className="text-sm text-gray-400 text-center py-8">加载中...</p>
              ) : unsettledSales.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">没有未结清的订单</p>
              ) : (
                <div className="space-y-2">
                  <button onClick={toggleSelectAll}
                    className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline mb-2">
                    {selectedSaleIds.length === unsettledSales.length
                      ? <><CheckSquare className="w-3.5 h-3.5" />取消全选</>
                      : <><Square className="w-3.5 h-3.5" />全选</>}
                  </button>
                  {unsettledSales.map(s => (
                    <label key={s.id} className="flex items-center justify-between border rounded-lg p-3 cursor-pointer hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <input type="checkbox" checked={selectedSaleIds.includes(s.id)}
                          onChange={() => toggleSelectSale(s.id)} className="w-4 h-4" />
                        <div>
                          <p className="text-sm text-gray-800">{s.order_no}</p>
                          <p className="text-xs text-gray-400">{new Date(s.created_at).toLocaleString('zh-CN')}</p>
                        </div>
                      </div>
                      <span className="font-medium text-sm">${parseFloat(s.total_amount).toFixed(2)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 border-t flex-shrink-0 space-y-3">
              {selectedSaleIds.length > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">已选 {selectedSaleIds.length} 笔，合计</span>
                  <span className="font-bold text-lg text-green-600">${selectedTotal.toFixed(2)}</span>
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => setSettleCustomer(null)} className="flex-1 px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">取消</button>
                <button onClick={handleSettle} disabled={selectedSaleIds.length === 0 || settling}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">
                  {settling ? '结清中...' : `确认结清（${selectedSaleIds.length}笔）`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditCustomersPage;